import { motion } from 'framer-motion'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { AiFillHome } from 'react-icons/ai'
import {
  FaCalendarAlt,
  FaClipboard,
  FaFolder,
  FaTasks,
  FaUser,
} from 'react-icons/fa'
import { MdCardMembership } from 'react-icons/md'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useBoard, useWorkspace } from '../../hooks'
import { cn } from '../../utils/cn'
import './Breadcrumbs.css'

export interface BreadcrumbItem {
  label: string
  path?: string
  icon?: React.ReactNode
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[]
  className?: string
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  className,
}) => {
  const location = useLocation()
  const params = useParams<{
    workspaceId?: string
    boardId?: string
    cardId?: string
  }>()

  const boardId = params.boardId ? Number(params.boardId) : 0
  const { data: board } = useBoard(boardId)

  // Workspace ID board ichidan olinadi (fallback — URL)
  const workspaceId = board?.workspaceId
    ? Number(board.workspaceId)
    : params.workspaceId
      ? Number(params.workspaceId)
      : 0

  const { data: workspace } = useWorkspace(workspaceId)

  const breadcrumbs =
    items || generateBreadcrumbs(location.pathname, params, workspace, board)

  if (breadcrumbs.length === 0) return null

  return (
    <nav className={cn('breadcrumbs', className)} aria-label="Breadcrumb">
      <ol className="breadcrumbs__list">
        {breadcrumbs.map((item, index) => {
          const isLast = index === breadcrumbs.length - 1

          return (
            <motion.li
              key={index}
              className="breadcrumbs__item"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              {!isLast && item.path ? (
                <Link to={item.path} className="breadcrumbs__link">
                  {item.icon && (
                    <span className="breadcrumbs__icon">{item.icon}</span>
                  )}
                  <span>{item.label}</span>
                </Link>
              ) : (
                <span className="breadcrumbs__current">
                  {item.icon && (
                    <span className="breadcrumbs__icon">{item.icon}</span>
                  )}
                  <span>{item.label}</span>
                </span>
              )}

              {!isLast && (
                <span className="breadcrumbs__separator" aria-hidden="true">
                  →
                </span>
              )}
            </motion.li>
          )
        })}
      </ol>
    </nav>
  )
}

/**
 * Generate breadcrumbs based on current path
 */
function generateBreadcrumbs(
  pathname: string,
  params: { workspaceId?: string; boardId?: string; cardId?: string },
  workspace?: { id: number; name: string } | null,
  board?: { id: number; name: string; workspaceId: number } | null
): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = []
  const { t } = useTranslation()

  // 1) Workspaces home
  breadcrumbs.push({
    label: t('workspace.title'),
    path: '/workspaces',
    icon: <AiFillHome />,
  })

  // 2) My Tasks page
  if (pathname === '/my-tasks') {
    breadcrumbs.push({
      label: t('myTasks.title', 'My Tasks'),
      icon: <FaTasks />,
    })
    return breadcrumbs
  }

  // 3) Calendar page
  if (pathname === '/calendar') {
    breadcrumbs.push({
      label: t('calendar.title'),
      icon: <FaCalendarAlt />,
    })
    return breadcrumbs
  }

  // Profile
  if (pathname === '/profile') {
    breadcrumbs.push({
      label: t('profile.title'),
      icon: <FaUser />,
    })
    return breadcrumbs
  }

  // 4) Workspace page
  if (workspace && pathname.startsWith('/workspaces/')) {
    breadcrumbs.push({
      label: workspace.name,
      path: `/workspaces/${workspace.id}`,
      icon: <FaFolder />,
    })
    return breadcrumbs
  }

  // Profile page

  // 5) Board (and card) pages
  if (board && pathname.startsWith('/boards/')) {
    // Parent workspace (always included)
    if (workspace) {
      breadcrumbs.push({
        label: workspace.name,
        path: `/workspaces/${workspace.id}`,
        icon: <FaFolder />,
      })
    }

    // Current board
    breadcrumbs.push({
      label: board.name,
      path: `/boards/${board.id}`,
      icon: <FaClipboard />,
    })

    // Card detail
    if (params.cardId) {
      breadcrumbs.push({
        label: `Card #${params.cardId}`,
        icon: <MdCardMembership />,
      })
    }
  }

  return breadcrumbs
}
